import React, {useEffect, useState, useMemo} from "react";
import {useNavigate} from "react-router-dom";
import "./EventList.css";
import countries from "../data/countries.json";

const EventList = ({tourId, tourName, onEdit, refreshKey}) => {
    const [events, setEvents] = useState([]);
    const [error, setError] = useState(null);
    const [feedbackMessage, setFeedbackMessage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [deletingEventId, setDeletingEventId] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [eventToDelete, setEventToDelete] = useState(null);
    
    // Filter and sort state
    const [filters, setFilters] = useState({
        assignedUser: '',
        country: '',
        searchTerm: ''
    });
    const [sortConfig, setSortConfig] = useState({
        key: 'event_date',
        direction: 'asc'
    });
    const navigate = useNavigate();
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const token = localStorage.getItem("token"); // Get token for authenticated requests
    const fetchEvents = () => {
        setLoading(true);
        fetch(`${apiUrl}/events/${tourId}`)
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Failed to fetch events");
                }
                return response.json();
            })
            .then((data) => {
                setEvents(data);
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching events:", error);
                setError("Failed to load events. Please try again.");
                setLoading(false);
            });
    };
    const handleDelete = async (eventId) => {
        setDeletingEventId(eventId);
        try {
            const url = `${apiUrl}/events/${eventId}`;
            const response = await fetch(url, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (!response.ok) {
                throw new Error("Failed to delete event.");
            }
            setFeedbackMessage("Event deleted successfully.");
            fetchEvents(); // Refresh the event list
        } catch (error) {
            console.error("Error deleting event:", error);
            setFeedbackMessage("Failed to delete event. Please try again.");
        } finally {
            setDeletingEventId(null);
        }
    };

    const openDeleteModal = (event) => {
        setEventToDelete(event);
        setShowDeleteModal(true);
    };


    // Get unique assigned users for filter dropdown
    const assignedUsers = useMemo(() => {
        const users = events
            .map(event => event.assigned_user_name)
            .filter(name => name && name !== "Not Assigned");
        return [...new Set(users)].sort();
    }, [events]);

    // Get unique countries for filter dropdown - use full countries list
    const availableCountries = useMemo(() => {
        // Get countries from existing events
        const eventCountries = events
            .map(event => event.country)
            .filter(country => country && country !== "Not Specified");
        
        // Get unique event countries
        const uniqueEventCountries = [...new Set(eventCountries)];
        
        // If we have event countries, use them; otherwise use full countries list
        if (uniqueEventCountries.length > 0) {
            return uniqueEventCountries.sort();
        } else {
            // Use full countries list from countries.json
            return countries.map(c => c.name).sort();
        }
    }, [events]);

    // Filter and sort events
    const filteredAndSortedEvents = useMemo(() => {
        let filtered = events.filter(event => {
            // Search term filter
            if (filters.searchTerm && !event.name.toLowerCase().includes(filters.searchTerm.toLowerCase())) {
                return false;
            }
            
            // Assigned user filter
            if (filters.assignedUser && event.assigned_user_name !== filters.assignedUser) {
                return false;
            }
            
            // Country filter
            if (filters.country && event.country !== filters.country) {
                return false;
            }
            

            
            return true;
        });

        // Sort events
        filtered.sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];
            
            // Handle date sorting
            if (sortConfig.key === 'event_date') {
                aValue = aValue ? new Date(aValue) : new Date(0);
                bValue = bValue ? new Date(bValue) : new Date(0);
            }
            
            // Handle string sorting
            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }
            
            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });

        return filtered;
    }, [events, filters, sortConfig]);

    const handleSort = (key) => {
        setSortConfig(prevConfig => ({
            key,
            direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            assignedUser: '',
            country: '',
            searchTerm: ''
        });
    };

    useEffect(() => {
        fetchEvents();
    }, [tourId, refreshKey]); // Re-fetch events when tourId or refreshKey changes

    if (loading) {
        return <p>Loading events...</p>;
    }

    if (error) {
        return <p className="text-danger">{error}</p>;
    }

    if (!events || events.length === 0) {
        return <p>No events available for this tour.</p>;
    }

    return (
        <div className="tours">
            {feedbackMessage && <p className="text-success">{feedbackMessage}</p>}
            {error && <p className="text-danger">{error}</p>}
            
            {/* Filters Section */}
            <div className="filters-section mb-3 p-3 border rounded">
                <h5>Filters & Sorting</h5>
                <div className="row">
                    <div className="col-md-3">
                        <label className="form-label">Search Event Name:</label>
                        <input
                            type="text"
                            className="form-control"
                            value={filters.searchTerm}
                            onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                            placeholder="Search events..."
                        />
                    </div>
                    <div className="col-md-2">
                        <label className="form-label">Assigned User:</label>
                        <select
                            className="form-control"
                            value={filters.assignedUser}
                            onChange={(e) => handleFilterChange('assignedUser', e.target.value)}
                        >
                            <option value="">All Users</option>
                            {assignedUsers.map(user => (
                                <option key={user} value={user}>{user}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-2">
                        <label className="form-label">Country:</label>
                        <select
                            className="form-control"
                            value={filters.country}
                            onChange={(e) => handleFilterChange('country', e.target.value)}
                        >
                            <option value="">All Countries</option>
                            {availableCountries.map(country => (
                                <option key={country} value={country}>{country}</option>
                            ))}
                        </select>
                    </div>

                    <div className="col-md-3 d-flex align-items-end">
                        <button
                            className="btn btn-secondary me-2"
                            onClick={clearFilters}
                        >
                            Clear Filters
                        </button>
                        <span className="text-muted">
                            Showing {filteredAndSortedEvents.length} of {events.length} events
                        </span>
                    </div>
                </div>
            </div>

            <table>
                <thead>
                <tr>
                    <th 
                        onClick={() => handleSort('name')}
                        style={{cursor: 'pointer'}}
                        className="sortable-header"
                        data-sort={sortConfig.key === 'name' ? sortConfig.direction : ''}
                    >
                        EVENT NAME
                    </th>
                    <th 
                        onClick={() => handleSort('city')}
                        style={{cursor: 'pointer'}}
                        className="sortable-header"
                        data-sort={sortConfig.key === 'city' ? sortConfig.direction : ''}
                    >
                        EVENT CITY
                    </th>
                    <th 
                        onClick={() => handleSort('country')}
                        style={{cursor: 'pointer'}}
                        className="sortable-header"
                        data-sort={sortConfig.key === 'country' ? sortConfig.direction : ''}
                    >
                        EVENT COUNTRY
                    </th>
                    <th 
                        onClick={() => handleSort('event_date')}
                        style={{cursor: 'pointer'}}
                        className="sortable-header"
                        data-sort={sortConfig.key === 'event_date' ? sortConfig.direction : ''}
                    >
                        EVENT DATE
                    </th>
                    <th 
                        onClick={() => handleSort('assigned_user_name')}
                        style={{cursor: 'pointer'}}
                        className="sortable-header"
                        data-sort={sortConfig.key === 'assigned_user_name' ? sortConfig.direction : ''}
                    >
                        ASSIGNED USER
                    </th>
                    <th>CREATED DATE</th>
                    <th>CREATED BY</th>
                    <th></th>
                </tr>
                </thead>
                <tbody>
                {filteredAndSortedEvents.map((event) => (
                    <tr key={event.id}>
                        <td>{event.name}</td>
                        <td>{event.city}</td>
                        <td>{event.country || "Not Specified"}</td>
                        <td>
                             {event.event_date
                                    ? new Date(event.event_date + "T00:00:00").toLocaleDateString("en-US")
                                    : "No Date"}
                        </td>
                        <td>
                            {event.assigned_user_name || "Not Assigned"}
                        </td>
                        <td>
                            {event.created_on
                                ? new Date(event.created_on).toLocaleDateString("en-US")
                                : "No Date"}
                        </td>
                        <td>{event.created_by}</td>
                        <td>
                            <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => onEdit(event)}
                            >
                                Edit
                            </button>
                            &nbsp;
                            <button
                                className="btn btn-sm btn-success"
                                onClick={() =>
                                    navigate(
                                        `/submissions/${event.id}/${tourName}/${tourId}/${encodeURIComponent(
                                            event.name
                                        )}`
                                    )
                                }
                            >
                                View Submissions
                            </button>
                            &nbsp;
                            <button
                                className="btn btn-sm btn-secondary"
                                onClick={() =>
                                    navigate(
                                        `/event/import/${event.id}`
                                    )
                                }
                            >
                                Import Submissions
                            </button>&nbsp;
                            <button
                                className="btn btn-sm btn-danger"
                                onClick={() => openDeleteModal(event)}
                                disabled={deletingEventId === event.id}
                            >
                                {deletingEventId === event.id ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                        Deleting...
                                    </>
                                ) : (
                                    'Delete'
                                )}
                            </button>
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 9999,
                    }}
                >
                    <DeleteConfirmationModal
                        item={eventToDelete}
                        itemType="event"
                        onConfirm={handleDelete}
                        onClose={() => setShowDeleteModal(false)}
                    />
                </div>
            )}
        </div>
    );
};

// Delete Confirmation Modal Component
const DeleteConfirmationModal = ({ item, itemType, onConfirm, onClose }) => {
    const [deleteText, setDeleteText] = React.useState('');
    const [isDeleting, setIsDeleting] = React.useState(false);

    const handleConfirm = async () => {
        if (deleteText !== 'DELETE') {
            return;
        }

        setIsDeleting(true);
        try {
            await onConfirm(item.id);
            onClose();
        } catch (error) {
            console.error(`Error deleting ${itemType}:`, error);
        } finally {
            setIsDeleting(false);
        }
    };

    const isDeleteButtonEnabled = deleteText === 'DELETE' && !isDeleting;

    return (
        <div
            style={{
                backgroundColor: "#fff",
                padding: "20px",
                borderRadius: "8px",
                width: "500px",
                maxHeight: "90vh",
                overflowY: "auto",
                position: "relative",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                    paddingBottom: "10px",
                    borderBottom: "1px solid #e0e0e0",
                }}
            >
                <h2 style={{ margin: 0, color: '#dc3545' }}>Delete {itemType.charAt(0).toUpperCase() + itemType.slice(1)}</h2>
                <button
                    onClick={onClose}
                    style={{
                        background: "none",
                        border: "none",
                        fontSize: "24px",
                        cursor: "pointer",
                        color: "#666",
                    }}
                >
                    ×
                </button>
            </div>
            
            <div className="modal-body">
                <div style={{ marginBottom: '20px' }}>
                    <p style={{ marginBottom: '15px', color: '#6c757d' }}>
                        You are about to delete the {itemType} <strong>"{item?.name}"</strong>. 
                        This action cannot be undone.
                    </p>
                    
                    <div style={{ 
                        padding: '15px', 
                        backgroundColor: '#fff3cd', 
                        border: '1px solid #ffeaa7',
                        borderRadius: '6px',
                        marginBottom: '20px'
                    }}>
                        <p style={{ margin: 0, color: '#856404' }}>
                            <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px' }}></i>
                            <strong>Warning:</strong> Deleting this {itemType} will:
                        </p>
                        <ul style={{ margin: '10px 0 0 20px', color: '#856404' }}>
                            {itemType === 'tour' ? (
                                <>
                                    <li>Remove all associated events</li>
                                    <li>Delete all event submissions</li>
                                    <li>Remove all event data</li>
                                </>
                            ) : (
                                <>
                                    <li>Remove all associated submissions</li>
                                    <li>Delete all submission data</li>
                                </>
                            )}
                        </ul>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                            To confirm deletion, type <strong>DELETE</strong> in the field below:
                        </label>
                        <input
                            type="text"
                            value={deleteText}
                            onChange={(e) => setDeleteText(e.target.value)}
                            placeholder="Type DELETE to confirm"
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                        />
                    </div>
                </div>
            </div>
            
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "10px",
                    padding: "20px",
                    borderTop: "1px solid #e0e0e0",
                }}
            >
                <button 
                    className="btn btn-secondary" 
                    onClick={onClose}
                    disabled={isDeleting}
                >
                    Cancel
                </button>
                <button 
                    className="btn btn-danger" 
                    onClick={handleConfirm}
                    disabled={!isDeleteButtonEnabled}
                    style={{
                        opacity: isDeleteButtonEnabled ? 1 : 0.6,
                        cursor: isDeleteButtonEnabled ? 'pointer' : 'not-allowed'
                    }}
                >
                    {isDeleting ? (
                        <>
                            <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                            Deleting...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-trash" style={{ marginRight: '8px' }}></i>
                            Delete {itemType.charAt(0).toUpperCase() + itemType.slice(1)}
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default EventList;